<script lang="ts">
  import { onMount } from 'svelte';
  import { toastStore, type ToastType } from '$lib/stores/toastStore';
  import { API_SERVER_ERROR_EVENT, getSafeServerErrorMessage } from '$lib/utils/apiFetch';

  let copyStatus: { toastId: number; copied: boolean } | null = null;

  onMount(() => {
    const handleServerError = (event: Event) => {
      const detail: unknown = (event as CustomEvent<unknown>).detail;
      if (typeof detail !== 'object' || detail === null || !('requestId' in detail)) return;
      const requestId = detail.requestId;
      if (typeof requestId === 'string') {
        const userMessage = 'userMessage' in detail
          ? getSafeServerErrorMessage(detail.userMessage) ?? undefined
          : undefined;
        toastStore.supportReference(requestId, userMessage);
      }
    };

    window.addEventListener(API_SERVER_ERROR_EVENT, handleServerError);
    return () => window.removeEventListener(API_SERVER_ERROR_EVENT, handleServerError);
  });

  function icon(type: ToastType): string {
    return type === 'success' ? '✓' : type === 'error' ? '!' : 'i';
  }

  async function copyReference(toastId: number, requestId: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(requestId);
      copyStatus = { toastId, copied: true };
    } catch {
      copyStatus = { toastId, copied: false };
    }
  }
</script>

<div class="toast-container" aria-live="polite" aria-atomic="true">
  {#each $toastStore as toast (toast.id)}
    <div class:success={toast.type === 'success'} class:error={toast.type === 'error'} class:info={toast.type === 'info'} class="toast" role={toast.type === 'error' ? 'alert' : 'status'}>
      <span class="toast-icon" aria-hidden="true">{icon(toast.type)}</span>
      <div class="toast-content">
        <span class="toast-message">{toast.message}</span>
        {#if toast.supportReference}
          <div class="support-reference">
            <span>Référence : <code>{toast.supportReference}</code></span>
            <button type="button" class="copy-reference" on:click={() => copyReference(toast.id, toast.supportReference!)}>
              {copyStatus?.toastId === toast.id ? (copyStatus.copied ? 'Copiée' : 'Copie impossible') : 'Copier la référence'}
            </button>
          </div>
        {/if}
      </div>
      <button type="button" class="toast-close" on:click={() => toastStore.remove(toast.id)} aria-label="Fermer la notification">×</button>
    </div>
  {/each}
</div>

<style>
  .toast-container { position: fixed; top: 1rem; right: 1rem; z-index: 1000; display: flex; flex-direction: column; gap: .65rem; width: min(360px, calc(100vw - 2rem)); pointer-events: none; }
  .toast { display: flex; align-items: center; gap: .65rem; padding: .8rem .85rem; border-radius: 10px; color: #25324a; background: white; box-shadow: 0 5px 18px #1f2a4433; border-left: 4px solid #667eea; pointer-events: auto; }
  .toast.success { border-left-color: #1a9b5f; }
  .toast.error { border-left-color: #d14343; }
  .toast-icon { display: inline-flex; align-items: center; justify-content: center; width: 1.35rem; height: 1.35rem; flex: 0 0 1.35rem; border-radius: 50%; color: white; background: #667eea; font-weight: 800; font-size: .85rem; }
  .toast.success .toast-icon { background: #1a9b5f; }
  .toast.error .toast-icon { background: #d14343; }
  .toast-content { flex: 1; min-width: 0; }
  .toast-message { display: block; font-size: .9rem; line-height: 1.35; }
  .support-reference { display: flex; align-items: center; justify-content: space-between; gap: .5rem; margin-top: .45rem; color: #526078; font-size: .78rem; }
  .support-reference code { color: #25324a; font-size: .76rem; overflow-wrap: anywhere; }
  .copy-reference { flex: 0 0 auto; padding: .25rem .45rem; border: 1px solid #dce1ef; border-radius: 6px; color: #35456a; background: #f7f8fc; font-size: .75rem; }
  .toast-close { padding: .1rem .35rem; color: #718096; background: transparent; font-size: 1.25rem; line-height: 1; }
  .toast-close:hover { color: #25324a; opacity: 1; }
  @media (max-width: 600px) { .toast-container { top: .75rem; right: .75rem; width: calc(100vw - 1.5rem); } }
</style>
